package com.rainbowclubs.rainbow.presentation.admin

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.rainbowclubs.rainbow.data.models.Event
import com.rainbowclubs.rainbow.presentation.ScreenState
import com.rainbowclubs.rainbow.presentation.common.ErrorScreen
import com.rainbowclubs.rainbow.presentation.common.LoadingScreen
import com.rainbowclubs.rainbow.presentation.theme.RainbowClubTheme
import com.rainbowclubs.rainbow.presentation.theme.accentGreen
import com.rainbowclubs.rainbow.presentation.theme.transparent
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import rainbowclubs.composeapp.generated.resources.Res
import rainbowclubs.composeapp.generated.resources.calendar_icon_cd
import rainbowclubs.composeapp.generated.resources.chevron_icon_cd
import rainbowclubs.composeapp.generated.resources.edit_event_icon_cd
import rainbowclubs.composeapp.generated.resources.edit_event_page_description
import rainbowclubs.composeapp.generated.resources.edit_existing_event
import rainbowclubs.composeapp.generated.resources.event_actions_icon_cd
import rainbowclubs.composeapp.generated.resources.general_error
import rainbowclubs.composeapp.generated.resources.ic_calendar
import rainbowclubs.composeapp.generated.resources.ic_chevron_right
import rainbowclubs.composeapp.generated.resources.ic_document_edit
import rainbowclubs.composeapp.generated.resources.no_events_to_edit_description
import rainbowclubs.composeapp.generated.resources.no_events_to_edit_title
import rainbowclubs.composeapp.generated.resources.select_event_to_edit
import rainbowclubs.composeapp.generated.resources.try_again_button

@Composable
fun EditEventScreen(
    clubId: String,
    viewModel: EditEventViewModel = koinViewModel(parameters = { parametersOf(clubId) }),
    navigateTo: (EditEventViewModel.NavigationEvent) -> Unit,
) {
    val screenState by viewModel.screenState.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.navigationIntent.collect { intent ->
            navigateTo(intent)
        }
    }

    Scaffold(
        containerColor = MaterialTheme.colorScheme.transparent,
    ) {
        Box(
            modifier = Modifier
                .padding(it)
                .fillMaxSize(),
        ) {
            when (val state = screenState) {
                is ScreenState.Loading -> LoadingScreen(Modifier.fillMaxSize())
                is ScreenState.Error -> ErrorScreen(
                    error = stringResource(Res.string.general_error),
                    onButtonClick = { viewModel.fetchEvents() },
                    buttonText = stringResource(Res.string.try_again_button),
                )

                is ScreenState.Content -> EditEventScreenContent(
                    uiState = state.data,
                    navigateTo = navigateTo,
                    onEventSelected = viewModel::selectEvent,
                    onLoadMore = viewModel::loadNextPage,
                )
            }
        }
    }
}

@Composable
private fun EditEventScreenContent(
    uiState: EditEventViewModel.UiState,
    navigateTo: (EditEventViewModel.NavigationEvent) -> Unit,
    onEventSelected: (Event) -> Unit,
    onLoadMore: () -> Unit,
) {
    val listState = rememberLazyListState()
    val shouldLoadMore by remember {
        derivedStateOf {
            val lastVisibleItemIndex = listState.layoutInfo.visibleItemsInfo.lastOrNull()?.index
                ?: return@derivedStateOf false
            val totalItemsCount = listState.layoutInfo.totalItemsCount

            totalItemsCount > 0 && lastVisibleItemIndex >= totalItemsCount - 4
        }
    }

    LaunchedEffect(
        shouldLoadMore,
        uiState.hasNextPage,
        uiState.isLoadingMore,
        uiState.loadMoreError
    ) {
        if (shouldLoadMore && uiState.hasNextPage && !uiState.isLoadingMore && !uiState.loadMoreError) {
            onLoadMore()
        }
    }

    LazyColumn(
        modifier = Modifier
            .fillMaxSize(),
        state = listState,
    ) {
        item {
            EventFormHeader(
                title = Res.string.edit_existing_event,
                description = Res.string.edit_event_page_description,
                icon = Res.drawable.ic_document_edit,
                iconContentDescription = Res.string.edit_event_icon_cd,
                onBackClick = { navigateTo(EditEventViewModel.NavigationEvent.Back) },
            )
        }

        item {
            Spacer(modifier = Modifier.height(52.dp))
        }

        item {
            EventFormSection(
                title = Res.string.select_event_to_edit,
                iconContentDescription = Res.string.event_actions_icon_cd,
                accent = MaterialTheme.colorScheme.accentGreen,
            )
        }

        item {
            Spacer(modifier = Modifier.height(28.dp))
        }

        if (uiState.events.isEmpty()) {
            item {
                NoEventsToEditCard()
            }
            item {
                Spacer(modifier = Modifier.height(36.dp))
            }
        } else {
            itemsIndexed(
                items = uiState.events,
                key = { index, event -> event.id ?: "${event.title}-${event.startsAt}-$index" },
            ) { _, event ->
                EventSelectionCard(
                    event = event,
                    onClick = { onEventSelected(event) },
                )
                Spacer(modifier = Modifier.height(16.dp))
            }

            if (uiState.isLoadingMore || uiState.loadMoreError) {
                item {
                    LoadMoreEventsFooter(
                        isLoading = uiState.isLoadingMore,
                        hasError = uiState.loadMoreError,
                        onRetry = onLoadMore,
                    )
                }
            }

            item {
                Spacer(modifier = Modifier.height(20.dp))
            }
        }
    }
}

@Composable
private fun EventSelectionCard(
    event: Event,
    onClick: () -> Unit,
) {
    val accent = MaterialTheme.colorScheme.primary

    Surface(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(28.dp),
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(
            width = 1.dp,
            color = MaterialTheme.colorScheme.outlineVariant,
        ),
    ) {
        Row(
            modifier = Modifier.padding(16.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier = Modifier
                    .size(56.dp)
                    .background(accent.copy(alpha = 0.12f), RoundedCornerShape(18.dp)),
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    painter = painterResource(Res.drawable.ic_calendar),
                    contentDescription = stringResource(Res.string.calendar_icon_cd),
                    modifier = Modifier.size(32.dp),
                    tint = accent,
                )
            }

            Spacer(modifier = Modifier.width(16.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = event.title,
                    color = MaterialTheme.colorScheme.onSurface,
                    style = MaterialTheme.typography.titleLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Spacer(modifier = Modifier.height(6.dp))
                Text(
                    text = "${formatEventDate(event.startsAt)} • ${formatEventTime(event.startsAt)}",
                    color = MaterialTheme.colorScheme.outline,
                    style = MaterialTheme.typography.titleMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }

            Spacer(modifier = Modifier.width(12.dp))

            Icon(
                painter = painterResource(Res.drawable.ic_chevron_right),
                contentDescription = stringResource(Res.string.chevron_icon_cd),
                modifier = Modifier.size(24.dp),
                tint = MaterialTheme.colorScheme.outline,
            )
        }
    }
}

@Composable
private fun NoEventsToEditCard() {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(24.dp),
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.padding(24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_calendar),
                contentDescription = stringResource(Res.string.calendar_icon_cd),
                modifier = Modifier.size(48.dp),
                tint = MaterialTheme.colorScheme.primary,
            )
            Spacer(modifier = Modifier.height(16.dp))
            Text(
                text = stringResource(Res.string.no_events_to_edit_title),
                color = MaterialTheme.colorScheme.onSurface,
                style = MaterialTheme.typography.titleLarge,
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = stringResource(Res.string.no_events_to_edit_description),
                color = MaterialTheme.colorScheme.outline,
                style = MaterialTheme.typography.titleMedium,
            )
        }
    }
}

@Composable
private fun LoadMoreEventsFooter(
    isLoading: Boolean,
    hasError: Boolean,
    onRetry: () -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 16.dp),
        contentAlignment = Alignment.Center,
    ) {
        when {
            isLoading -> CircularProgressIndicator(
                modifier = Modifier.size(28.dp),
                color = MaterialTheme.colorScheme.primary,
                strokeWidth = 3.dp,
            )

            hasError -> Column(
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Text(
                    text = stringResource(Res.string.general_error),
                    color = MaterialTheme.colorScheme.outline,
                    style = MaterialTheme.typography.titleMedium,
                )
                Spacer(modifier = Modifier.height(4.dp))
                TextButton(onClick = onRetry) {
                    Text(
                        text = stringResource(Res.string.try_again_button),
                        color = MaterialTheme.colorScheme.primary,
                        style = MaterialTheme.typography.titleMedium,
                    )
                }
            }
        }
    }
}

@Preview(name = "Edit Event Screen")
@Composable
private fun EditEventScreenPreview() {
    RainbowClubTheme {
        Surface {
            Box(modifier = Modifier.padding(24.dp)) {
                EditEventScreenContent(
                    uiState = EditEventViewModel.UiState(
                        events = listOf(
                            mockEditableEvent,
                            mockEditableEvent.copy(id = "2", title = "Community Dinner")
                        ),
                    ),
                    navigateTo = {},
                    onEventSelected = {},
                    onLoadMore = {},
                )
            }
        }
    }
}

private val mockEditableEvent = Event(
    id = "1",
    clubId = "club-id",
    startsAt = 1_772_000_000_000,
    endsAt = 1_772_012_600_000,
    description = "Games, snacks, and community time",
    title = "Board Game Night",
    comments = emptyList(),
    attendees = emptyList(),
    location = "Rainbow Club Room",
)
